const swea = require('swea');
const HTTP = swea.editor.HTTP;
const ModelManager = swea.editor.ModelManager;


/**
 * Class for performance profiling.
 * @class
 */
class Profiler {
  /**
   * Timeout in ms before flying after static view.
   * @type { number }
   * @static
   */
  static BEFORE_FLY_TIMEOUT = 20000;

  /**
   * Timeout in ms after flying before downloading log and gather next.
   * @type { number }
   * @static
   */
  static AFTER_FLY_TIMEOUT = 30000;

  /**
   * Timeout in ms for freeing up memory before going to next config.
   * @type { number }
   * @static
   */
  static FREE_MEMORY_TIMEOUT = 5000;
  
  /**
   * The constructor.
   * @param { swea.scene.viewers.Viewer } sceneViewer The SWEA scene viewer.
   * @param { string } modelPath The model path, mapped to hard-coded routes.
   * @param { number[] } initialEye The initial eye position as an array of x,y,z.
   * @param { number[] } initialCenter The initial center position as an array of x,y,z.
   * @param { boolean } changeTextureSubloading True to change texture subloading parameters between profiling sessions.
   * @param { boolean } changeGeometrySubloading True to change geometry subloading parameters between profiling sessions.
   */
  constructor(sceneViewer, modelPath, initialEye, initialCenter, changeTextureSubloading, changeGeometrySubloading) {
    this._sceneViewer = sceneViewer;

    if (changeTextureSubloading === changeGeometrySubloading) {
      console.error('Profiler needs to change either texture or geometry subloading configuration.');
    }

    let dirName = swea.util.getUrlDirname(modelPath);
    if (dirName.endsWith('/')) {
      dirName = dirName.substring(0, dirName.length - 1);
    }
    this._modelName = swea.util.getUrlBasename(dirName);
    if (this._modelName === 'swea') {
      dirName = swea.util.getUrlDirname(dirName);
      this._modelName = swea.util.getUrlBasename(dirName.substring(0, dirName.length - 1));
    }
    
    this._initialEye = initialEye;
    this._initialCenter = initialCenter;
    this._changeTextureSubloading = changeTextureSubloading;
    this._changeGeometrySubloading = changeGeometrySubloading;
    this._debugStats= '';
    this._debugFlyDelay = 0;
    this._poses = [];
    this._setPoses();
    
    this._subloadTextureBudgets = [0.0025, 0.005, 0.007, 0.01];
    this._subloadTextureSizes = [4, 8, 16, 32, 64, 128];
    
    this._subloadGeometryBudgets = [0.001, 0.004];
    this._subloadGeometrySizes = [1024, 4096, 16384, 32768];
  }

  /**
   * Flies the route while measuring frame drops.
   * @returns { Promise } A promise resolving when route is finished.
   * @public
   */
  fly() {
    if (this._poses.length === 0) {
      throw new Error('No profiler poses for model ' + this._modelName);
    }
    
    // Measure frame drops (wait 100ms to avoid measuring first drops)
    setTimeout(() => {
      this._sceneViewer.getOsgViewer().setFrameDropTargetFps(60);
    }, 100);

    return new Promise((resolve, reject) => {
      this._sceneViewer.setPagerReadyCallback(() => {
        this._sceneViewer.removePagerReadyCallback();
        this._flyToNextPose().then(() => {
          resolve();
        }).catch((err) => {
          reject(err);
        });
      });
    });
  }
  
  /**
   * Measures the pager time of the current static view.
   * @public
   */
  measureStaticPagerTime() {
    const startTime = Date.now();
    this._sceneViewer.setPagerReadyCallback(() => {
      console.info('Static pager ready at total ' + ((Date.now() - startTime) / 1000.0).toString() + ' s');
    });
  }

  /**
   * Flies to the next pose.
   * @returns { Promise } A promise resolving when route is finished.
   * @private
   */
  _flyToNextPose() {
    return new Promise((resolve, reject) => {
      const pose = this._poses.pop();
      if (pose !== undefined) {
        const eye = [0, 0, 0];
        const center = [0, 0, 0];
        this._sceneViewer.getSimpleView(eye, center);

        const turnAway = false;
        if (turnAway) {
          // Turn view away from target first (to stress)
          const oppositeCenter = swea.util.addVectors(eye, swea.util.subVectors(eye, pose.center));
          this._sceneViewer.flyToSimple(eye, oppositeCenter).then(() => {
            setTimeout(() => {
              // Turn view to new target
              this._sceneViewer.flyToSimple(eye, pose.center).then(() => {
                setTimeout(() => {
                  // Then fly to it
                  this._sceneViewer.flyToSimple(pose.eye, pose.center).finally(() => {
                    if (this._poses.length > 0) {
                      setTimeout(() => {
                        this._flyToNextPose().then(() => {
                          resolve();
                        }).catch((err) => {
                          reject(err);
                        });
                      }, this._debugFlyDelay);
                    }
                    else {
                      this._sceneViewer.setPagerReadyCallback(() => {
                        this._sceneViewer.removePagerReadyCallback();
                        this._sceneViewer.getOsgViewer().reportFrameDrops();
                        resolve();
                      });
                    }
                  });
                }, this._debugFlyDelay);
              }).catch((err) => {
                reject(err);
              });
            }, this._debugFlyDelay);
          }).catch((err) => {
            reject(err);
          });
        }
        else {
          this._sceneViewer.flyToSimple(pose.eye, pose.center).then(() => {
            if (this._poses.length > 0) {
              setTimeout(() => {
                this._flyToNextPose().then(() => {
                  resolve();
                }).catch((err) => {
                  reject(err);
                });
              }, this._debugFlyDelay);
            }
            else {
              const startTime = Date.now();
              let frameDropsReported = false;
              this._sceneViewer.setPagerReadyCallback(() => {
                if (!frameDropsReported) {
                  this._sceneViewer.getOsgViewer().reportFrameDrops();
                  frameDropsReported = true;
                }
                console.info('Pager "ready" at total ' + ((Date.now() - startTime) / 1000.0).toString() + ' s');
                resolve();
              });
            }
          }).catch((err) => {
            reject(err);
          });
        }
      }
    });
  }

  /**
   * Repeats profiler route flying with different subload configurations.
   * @public
   */
  repeatProfiler() {
    // Perform current profiler, except from first time without config where we just go to the first config
    const subloadTextureBudget = swea.util.getQueryParameters().texsubloadbudget;
    const subloadTextureSize = swea.util.getQueryParameters().texsubloadsize;
    const subloadGeometryBudget = swea.util.getQueryParameters().geomsubloadbudget;
    const subloadGeometrySize = swea.util.getQueryParameters().geomsubloadsize;
    
    let valid = true;
    if (this._changeTextureSubloading && (subloadTextureBudget === undefined || subloadTextureSize === undefined)) {
      valid = false;
    }
    if (this._changeGeometrySubloading && (subloadGeometryBudget === undefined || subloadGeometrySize === undefined)) {
      valid = false;
    }
    
    if (valid) {
      // Profiling with current budget and size
      let infoString = 'SUBLOAD: ';
      if (this._changeTextureSubloading) {
        infoString += ('texture budget: ' + subloadTextureBudget + ', texture size: ' + subloadTextureSize);
      }
      if (this._changeGeometrySubloading) {
        infoString += ('geometry budget: ' + subloadGeometryBudget + ', geometry size: ' + subloadGeometrySize);
      }
      console.info(infoString);
      this._captureConsole(subloadTextureBudget, subloadTextureSize, subloadGeometryBudget, subloadGeometrySize);
      this._doRepeatProfiler();
    }
    else if (swea.util.getQueryParameters().texsubload === '0' || swea.util.getQueryParameters().geomsubload === '0') {
      // Last time w/o subloading
      console.info('NO SUBLOAD');
      this._captureConsole(subloadTextureBudget, subloadTextureSize, subloadGeometryBudget, subloadGeometrySize);
      this._doRepeatProfiler();
    }
    else {
      // First time, go directly to next URL
      this._gotoNextProfilerUrl();
    }
  }

  /**
   * Performs the repeated profiler and then goes to next URL.
   * @private
   */
  _doRepeatProfiler() {
    // Start by measuring static pager time
    if (this._initialEye !== undefined && this._initialCenter !== undefined) {
      this._sceneViewer.setSimpleView(this._initialEye, this._initialCenter);
    }
    this.measureStaticPagerTime();
    
    // Wait 10s and then start flying
    setTimeout(() => {
      this.fly().then(() => {
        // Download log and gather next stats after 10s
        setTimeout(() => {
          this._gotoNextProfilerUrl();
        }, Profiler.AFTER_FLY_TIMEOUT);
      });
    }, Profiler.BEFORE_FLY_TIMEOUT);
  }
  
  /**
   * Fills list of subload configuration string indices from URL.
   * @param { string } url The URL.
   * @param { string } prefix The prefix ('tex' or 'geom').
   * @param { number[] } indices List of string indices.
   * @private
   */
  _fillUrlIndices(url, prefix, indices) {
    const budgetIndex = url.indexOf('&' + prefix + 'subloadbudget=');
    if (budgetIndex > -1) {
      indices.push(budgetIndex);
    }
    const afterBudgetIndex = (budgetIndex > -1 ? url.indexOf('&', budgetIndex + 1) : -1);
    if (afterBudgetIndex > -1) {
      indices.push(afterBudgetIndex);
    }
    const sizeIndex = url.indexOf('&' + prefix + 'subloadsize=');
    if (sizeIndex > -1) {
      indices.push(sizeIndex);
    }
    const afterSizeIndex = (sizeIndex > -1 ? url.indexOf('&', sizeIndex + 1) : -1);
    if (afterSizeIndex > -1) {
      indices.push(afterSizeIndex);
    }
    const subloadIndex = url.indexOf('&' + prefix + 'subload=');
    if (subloadIndex > -1) {
      indices.push(subloadIndex);
    }
    const afterSubloadIndex = (subloadIndex > -1 ? url.indexOf('&', subloadIndex + 1) : -1);
    if (afterSubloadIndex > -1) {
      indices.push(afterSubloadIndex);
    }
  }

  /**
   * Calculates a URL stripped from subload configiration from the current URL.
   * @returns { string } The stripped URL.
   * @private
   */
  _stripUrlFromSubload() {
    const url = window.location.href;
    
    const sortedIndices = [0];
    this._fillUrlIndices(url, 'tex', sortedIndices);
    this._fillUrlIndices(url, 'geom', sortedIndices);
    
    sortedIndices.sort();
    let strippedUrl = '';
    for (let i = 1; i < sortedIndices.length; i+=2) {
      strippedUrl += url.substring(sortedIndices[i - 1], sortedIndices[i]);
    }
    if (sortedIndices.length % 2 !== 0) {
      strippedUrl += url.substring(sortedIndices[sortedIndices.length - 1]);
    }    
    
    return strippedUrl;
  }

  /**
   * Goes to the next repeated profiler URL.
   * @private
   */
  _gotoNextProfilerUrl() {
    // Get URL stripped from subload config
    const strippedUrl = this._stripUrlFromSubload();

    // Calculate next config (or if last non-subload, just write log and quit)
    if (swea.util.getQueryParameters().texsubload === '0' || swea.util.getQueryParameters().geomsubload === '0') {
      HTTP.downloadTextData(this._modelName + '_no_subload.txt', this._debugStats);
      throw new Error('DONE GATHERING DEBUG STATS');
    }
    // Write log and find current subload config from URL
    const prevSubloadTextureBudget = swea.util.getQueryParameters().texsubloadbudget;
    const prevSubloadTextureSize = swea.util.getQueryParameters().texsubloadsize;
    const prevSubloadGeometryBudget = swea.util.getQueryParameters().geomsubloadbudget;
    const prevSubloadGeometrySize = swea.util.getQueryParameters().geomsubloadsize;

    let download = true;
    if (this._changeTextureSubloading && (prevSubloadTextureBudget === undefined || prevSubloadTextureSize === undefined)) {
      download = false;
    }
    if (this._changeGeometrySubloading && (prevSubloadGeometryBudget === undefined || prevSubloadGeometrySize === undefined)) {
      download = false;
    }

    if (download) {
      let path = this._modelName;
      if (this._changeTextureSubloading) {
        path += ('_texbudget_' + prevSubloadTextureBudget + '_texsize_' + prevSubloadTextureSize);
      }
      if (this._changeGeometrySubloading) {
        path += ('_geombudget_' + prevSubloadGeometryBudget + '_geomsize_' + prevSubloadGeometrySize);
      }
      path += '.txt';
      HTTP.downloadTextData(path, this._debugStats);
    }
    
    // If changing texture subload parameters, find next subload texture budget and size
    let subloadTextureBudget = undefined;
    let subloadTextureSize = undefined;
    if (this._changeTextureSubloading) {
      if (prevSubloadTextureBudget === undefined) {
        // Use first budget if no config
        subloadTextureBudget = this._subloadTextureBudgets[0];
      }
      else {
        // Use previous budget for now, may be updated below if sizes are exhausted
        subloadTextureBudget = prevSubloadTextureBudget;
      }

      if (prevSubloadTextureSize === undefined) {
        // Use first size if no config
        subloadTextureSize = this._subloadTextureSizes[0];
      }
      else {
        // Find next size
        for (let i = 0; i < this._subloadTextureSizes.length - 1; ++i) {
          if (this._subloadTextureSizes[i].toString() === prevSubloadTextureSize) {
            subloadTextureSize = this._subloadTextureSizes[i + 1];
            break;
          }
        }
      }
      
      if (subloadTextureSize === undefined) {
        // Start over with new budget
        subloadTextureBudget = undefined;
        subloadTextureSize = this._subloadTextureSizes[0];
        for (let i = 0; i < this._subloadTextureBudgets.length - 1; ++i) {
          if (this._subloadTextureBudgets[i].toString() === prevSubloadTextureBudget) {
            subloadTextureBudget = this._subloadTextureBudgets[i + 1];
            break;
          }
        }
      }
    }
    
    // If changing geometry subload parameters, find next subload geometry budget and size
    let subloadGeometryBudget = undefined;
    let subloadGeometrySize = undefined;
    if (this._changeGeometrySubloading) {
      if (prevSubloadGeometryBudget === undefined) {
        // Use first budget if no config
        subloadGeometryBudget = this._subloadGeometryBudgets[0];
      }
      else {
        // Use previous budget for now, may be updated below if sizes are exhausted
        subloadGeometryBudget = prevSubloadGeometryBudget;
      }
      
      if (prevSubloadGeometrySize === undefined) {
        // Use first size if no config
        subloadGeometrySize = this._subloadGeometrySizes[0];
      }
      else {
        // Find next size
        for (let i = 0; i < this._subloadGeometrySizes.length - 1; ++i) {
          if (this._subloadGeometrySizes[i].toString() === prevSubloadGeometrySize) {
            subloadGeometrySize = this._subloadGeometrySizes[i + 1];
            break;
          }
        }
      }
      
      if (subloadGeometrySize === undefined) {
        // Start over with new budget
        subloadGeometryBudget = undefined;
        subloadGeometrySize = this._subloadGeometrySizes[0];
        for (let i = 0; i < this._subloadGeometryBudgets.length - 1; ++i) {
          if (this._subloadGeometryBudgets[i].toString() === prevSubloadGeometryBudget) {
            subloadGeometryBudget = this._subloadGeometryBudgets[i + 1];
            break;
          }
        }
      }
    }
    
    let nextUrl = '';
    if (this._changeTextureSubloading) {
      if (subloadTextureBudget === undefined) {
        // We're done with subloading, do one last without
        nextUrl = strippedUrl + '&texsubload=0'
      }
      else {
        // Next budget and size
        nextUrl = strippedUrl + '&texsubload=1&texsubloadbudget=' + subloadTextureBudget.toString() + '&texsubloadsize=' + subloadTextureSize.toString();
      }
    }
    else if (this._changeGeometrySubloading) {
      if (subloadGeometryBudget === undefined) {
        // We're done with subloading, do one last without
        nextUrl = strippedUrl + '&geomsubload=0'
      }
      else {
        // Next budget and size
        nextUrl = strippedUrl + '&geomsubload=1&geomsubloadbudget=' + subloadGeometryBudget.toString() + '&geomsubloadsize=' + subloadGeometrySize.toString();
      }
    }
    
    // Free up memory and go to next
    const baseModel = ModelManager.instance().getBaseModel();
    if (baseModel !== undefined) {
      ModelManager.instance().removeModel(baseModel.getId());
    }
    this._sceneViewer.stop();
    setTimeout(() => {
      window.location = nextUrl;
    }, Profiler.FREE_MEMORY_TIMEOUT);
  }
  
  /**
   * Captures console.info in a string.
   * @param { number[] } subloadTextureBudget An array of texture subload budgets in ms.
   * @param { number[] } subloadTextureSize An array of texture subload sizes in px.
   * @param { number[] } subloadGeometryBudget An array of geometry subload budgets in ms.
   * @param { number[] } subloadGeometrySize An array of geometry subload sizes as number of elements.
   * @private
   */
  _captureConsole(subloadTextureBudget, subloadTextureSize, subloadGeometryBudget, subloadGeometrySize) {
    // Capture console.info
    this._debugStats = '========== PROFILER STATISTICS ==========\n';
    if (this._changeTextureSubloading && subloadTextureBudget !== undefined && subloadTextureSize !== undefined) {
      const budgetString = (subloadTextureBudget * 1000).toString() + 'ms';
      const sizeString = subloadTextureSize.toString() + 'px';
      this._debugStats += 'BUDGET: ' + budgetString + '\n';
      this._debugStats += 'SIZE: ' + sizeString + '\n';
    }
    else if (this._changeGeometrySubloading && subloadGeometryBudget !== undefined && subloadGeometrySize !== undefined) {
      const budgetString = (subloadGeometryBudget * 1000).toString() + 'ms';
      const sizeString = subloadGeometrySize.toString();
      this._debugStats += 'BUDGET: ' + budgetString + '\n';
      this._debugStats += 'SIZE: ' + sizeString + '\n';
    }
    else {
      this._debugStats += 'NO SUBLOAD\n';
    }
    console.info = (message) => {
      this._debugStats += message + '\n';
    };
  }

  /**
   * Sets hard-coded route poses from a given model name.
   * @private
   */
  _setPoses() {
    if (this._modelName.indexOf('po-box-model') > -1) {
      this._debugFlyDelay = 4000;
      this._poses = [
        {
          eye: [-5.8273681483727255, -44.81613072075214, 52.4809236940928],
          center: [-7.787829230983375, -26.387676257500118, 49.573362395168914]
        },
        {
          eye: [23.756061125434602, -11.188308690875871, 104.71046412907802],
          center: [19.73832278180927, -4.264842275901116, 55.19283119923125]
        },
        {
          eye: [5.523016347557046, 10.154384923497226, 50.358825100451384],
          center: [5.796037694388479, 7.275924466990321, 49.921953356625906]
        },
        {
          eye: [-63.09957934546508, 13.278728712155097, 78.7652861182654],
          center: [-8.41625368927186, -4.618079501236782, 57.76131475112279]
        },
        {
          eye: [-16.993549184808177, -23.22626620409172, 51.912276189776975],
          center: [-15.692610075010979, -23.28754881372859, 51.674892455412284]
        }
      ];
      this._poses.reverse();
    }
    else if (this._modelName.indexOf('norrbergavagen2024') > -1) {
      this._debugFlyDelay = 3000;
      this._poses = [
        {
          eye: [0.7250676871397137,-30.52345024072938,107.26740342555479],
          center: [0.7250676871397177,1.7559097310170544,96.77920358894231]
        },
        {
          eye: [-8.776890535098918, 5.655702461792522, 99.56558994178582],
          center: [-7.9321705327036955, 9.759022857141378, 99.64553995003857]
        },
        {
          eye: [-15.70002680925324, 5.065535496588927, 102.59017905181555],
          center: [-5.882521184334493, 4.553084854650333, 100.17480643219285]
        },
        {
          eye: [12.883524800430418, 5.97664353986717, 99.99848425704263],
          center: [11.272607101085368, 5.804803915936441, 99.62511840717465]
        },
        {
          eye: [5.406375002622288, 90.81967575926626, 135.1280515461644],
          center: [4.126841574714164, 6.0178602095844465, 99.62511840717465]
        },
        {
          eye: [-5.8967223699945475, -3.2096493045325984, 99.6078468773229],
          center: [-5.203050450877764, -5.109028795799616, 98.94860676350062]    
        }
      ];
      this._poses.reverse();
    }
    else if (this._modelName.indexOf('alfredshall') > -1) {
      this._debugFlyDelay = 7000;
      this._poses = [
        {
          eye: [10.276439122007735,15.039779390014443,5.301571989987671],
          center: [8.48701455321072,13.742136805804183,0.47706824227656774]
        },
        {
          eye: [1.5024491321084004,11.980892017088358,1.8953371114087845],
          center: [0.5402873974244304,11.192875737777266,1.3844720382863478]
        },
        {
          eye: [9.234929422099206,7.145319181825114,1.3387679915977153],
          center: [9.256860143682626,6.1662894070304315,0.9758530921566604]
        },
        {
          eye: [9.403128229844024,7.598351823615583,1.5921837882833294],
          center: [9.253568866417513,7.191141683937096,1.3435637992348124]
        },
        {
          eye: [7.709867924774391,6.482414793324766,1.2077040384278006],
          center: [7.445979700294602,6.144673066698641,0.9502287365510809]
        },
        {
          eye: [7.75083388668199,5.681804359853651,1.3747940563682812],
          center: [7.494466720711365,5.894384983214378,1.0018525666091573]
        },
        {
          eye: [9.776820424186916,8.632583651939107,1.0724433364206987],
          center: [9.611375757286229,8.779057983014074,0.6239198336582884]
        },
        {
          eye: [6.810961350093266,9.63084456414605,0.8213737359727612],
          center: [6.412754357000832,9.393096013235057,0.6345400741449063]
        },
        {
          eye: [7.490487980036587,7.692997717814544,0.920346125462491],
          center: [7.534453695563918,7.269863030475378,0.6576218360856689]
        },
        {
          eye: [7.707786436760168,7.645392412040244,0.9492335394878585],
          center: [7.514385399975987,7.237242624425473,0.7347346121226571]
        },
        {
          eye: [1.1202859082485728,10.312309380799633,0.9267502604111961],
          center: [0.4280175009648204,10.018990339424583,0.635451224099862]
        },
        {
          eye: [0.572013172583516,11.231444762540706,1.038333025171629],
          center: [0.41754557447607393,10.80936152826929,0.8192770860101929]
        },
        {
          eye: [2.6016677818529126,15.199654660303075,0.9549991452588581],
          center: [2.318974617046853,15.396353959574736,0.5527525751906359]
        },
        {
          eye: [2.1028639101206297,15.61110999951649,0.9034700402588615],
          center: [2.3448376844410586,15.349634339514973,0.5526436276729546]
        },
        {
          eye: [1.8681893632684803, 15.25506509212913, 0.670385019597111],
          center: [2.3448376844410586, 15.349634339514973, 0.5526436276729546]
        }
      ];
      this._poses.reverse();
    }
    else if (this._modelName.indexOf('concrete-silos') > -1) {
      this._debugFlyDelay = 2000;
      this._poses = [
        {
          eye: [1.9241422873539942, 17.620073533343824, 19.813986017259523],
          center: [2.0849844758035587, 16.151750101411807, 19.736004491180605]
        },
        {
          eye: [-230.02554639250536, 244.2200405556823, 189.7949707023648],
          center: [-48.066716356249614, 39.348186591840665, 4.359164512816717]
        },
        {
          eye: [-108.47861390839304, 4.369756358856469, 14.014192210091165],
          center: [-95.0407100696533, 3.5009170501208455, -2.1086179629639887]
        },
        {
          eye: [-108.47861390839304, 4.369756358856469, 14.014192210091165],
          center: [-95.0407100696533, 3.5009170501208455, -2.1086179629639887]
        },
        {
          eye: [311.6729458841191, 7.290640618496701, 112.2033375217992],
          center: [-1.7599764563230622, -9.172142571964647, -4.502814538425829]
        },
        {
          eye: [-9.493065074624926, -1.8698120589463663, 12.666865897413635],
          center: [-9.548866895717946, 0.5278661534210001, 12.240362858870771]
        }
      ];
    }
    else if (this._modelName.indexOf('teknikringen8_singlehouse') > -1) {
      this._debugFlyDelay = 3000;
      this._poses = [
        {
          eye: [787.2539094936395,813.9882091721182,136.6406133319407],
          center: [962.8538915302737,995.9814347633937,16.488075288022642]
        },
        {
          eye: [986.9843378577473,991.6936571504385,22.66995741624909],
          center: [987.8047851665923,1008.9425497834031,19.105907461040307]
        }
      ];
    }
    else if (this._modelName.indexOf('unicon-thin-labels') > -1) {
      this._debugFlyDelay = 12000;
      this._poses = [
        {
          eye: [-9.727622520174869, -22.72293742072629, 81.9068344202275],
          center: [-9.483868921919504, -12.875752546720229, 80.16928397651937]
        },
        {
          eye: [-11.679355003026771, -7.379953015020874, 85.36122310396554],
          center: [-10.524515266118659, -7.546741229997142, 84.913901374226]
        },
        {
          eye: [-2.310648435140855, -0.3851112500120397, 120.02691480345885],
          center: [-2.8260086361312533, -7.1420222094738905, 96.479822757069]
        },
        {
          eye: [5.911878848546911, -13.786641373148397, 99.52375512825672],
          center: [5.537683440634575, -13.45569703877542, 99.5450540541392]
        }
      ];
      this._poses.reverse();
    }
  }
}

export default Profiler;
